import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getInboundDocument, listInboundDocuments } from '../api/as2-inbound-documents';
import { useAsyncData } from '../hooks/useAsyncData';
import { ErrorState, EmptyState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { CopyButton } from '../components/CopyButton';
import { InboundPayloadDialog } from '../components/InboundPayloadDialog';
import type { As2InboundProcessingStatus, InboundAs2DocumentDetails, InboundAs2DocumentSummary, InboundDocumentListQuery } from '../types/api';

const processingStatuses: As2InboundProcessingStatus[] = ['PERSISTED', 'PROCESSED', 'FAILED'];

// The filter now matches the resolved `classification` column (see postgres-as2-bridge
// .repository.ts's listInboundDocuments), not just the raw documentType hint -- so these values
// must be the actual InboundRouteTarget strings (apps/integration/backend/src/application/as2/
// inbound-routing.ts), not guessed/abbreviated labels. The previous list (DUM, DUM_CC, CR, CUSRES,
// CUSCAR, ACK_IMGSCAN, ACK_SCANNER) didn't match any real stored value -- e.g. real DUM documents
// classify as DUM_CUSDEC, never bare "DUM" -- so filtering by them silently returned almost
// nothing. Verified against every distinct classification actually present in the live database.
const inboundDocumentTypes = [
  'AMLV', 'CECOR', 'COREOR_BAD_CUSRES', 'CTCA', 'CUSDEC_ML', 'CUSRES_ROUTING_CONFLICT',
  'CUSTOMS_CUSRES', 'DUM_CR', 'DUM_CUSDEC', 'ETAT_CHARGEMENT', 'IFTMAN_ADII_CUSRES',
  'IMAGE_SCANNER_ACK', 'MARITIME_EDIFACT', 'PASSAGE_SCANNER', 'PESAGE_ACK',
  'RESULTAT_SCANNER_ACK', 'TRYPTIQUE', 'UNSUPPORTED',
] as const;

// 611ADII00100 is ADII's standard partner code; the bare "ADII" form also appears on some
// historical/manually-submitted records.
const inboundPartnerCodes = ['611ADII00100', 'ADII'] as const;

// Pre-applies filters carried in the incoming URL (e.g. a cross-link from an AS2 transport
// message detail page — see src/lib/crossLinks.ts), read once on first render, mirroring the
// pattern already used by the AS2 message list pages.
function initialFiltersFromSearch(search: string): InboundDocumentListQuery {
  const params = new URLSearchParams(search);
  return {
    page: 1,
    pageSize: 20,
    sortBy: (params.get('inboundSortBy') ?? 'receivedAt') as NonNullable<InboundDocumentListQuery['sortBy']>,
    sortOrder: (params.get('inboundSortOrder') ?? 'desc') as 'asc' | 'desc',
    partnerCode: params.get('partnerCode') ?? '',
    documentType: params.get('inboundDocumentType') ?? '',
    processingStatus: (params.get('processingStatus') ?? '') as As2InboundProcessingStatus | '',
    as2MessageId: params.get('as2MessageId') ?? '',
    correlationId: params.get('correlationId') ?? '',
    receivedFrom: params.get('receivedFrom') ?? '',
    receivedTo: params.get('receivedTo') ?? '',
  };
}

function normalizeDateTimeLocal(value: string): string {
  return value ? new Date(value).toISOString() : '';
}

function toDateTimeLocalValue(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function InboundMessagesPage() {
  return <InboundMessagesPanel />;
}

export function InboundMessagesPanel() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => {
    const params = new URLSearchParams(search);
    return Boolean(params.get('as2MessageId') || params.get('correlationId') || params.get('receivedFrom') || params.get('receivedTo'));
  });
  const [filters, setFilters] = useState<InboundDocumentListQuery>(() => initialFiltersFromSearch(search));
  const [payloadDocument, setPayloadDocument] = useState<InboundAs2DocumentSummary | null>(null);
  const [payloadDetails, setPayloadDetails] = useState<InboundAs2DocumentDetails | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const payloadRequestId = useRef(0);

  const { data, isLoading, error, refresh } = useAsyncData(
    () => listInboundDocuments(filters),
    [JSON.stringify(filters)],
    { debounceMs: 250 },
  );

  useEffect(() => {
    const params = new URLSearchParams(search);
    params.set('view', 'inbound');
    const values: Array<[string, string | undefined]> = [
      ['partnerCode', filters.partnerCode], ['inboundDocumentType', filters.documentType],
      ['processingStatus', filters.processingStatus], ['as2MessageId', filters.as2MessageId], ['correlationId', filters.correlationId],
      ['receivedFrom', filters.receivedFrom], ['receivedTo', filters.receivedTo],
    ];
    values.forEach(([key, value]) => value ? params.set(key, value) : params.delete(key));
    if (filters.sortBy && filters.sortBy !== 'receivedAt') params.set('inboundSortBy', filters.sortBy); else params.delete('inboundSortBy');
    if (filters.sortOrder && filters.sortOrder !== 'desc') params.set('inboundSortOrder', filters.sortOrder); else params.delete('inboundSortOrder');
    const nextSearch = params.toString();
    if (nextSearch !== new URLSearchParams(search).toString()) navigate({ search: `?${nextSearch}` }, { replace: true });
  }, [filters, navigate, search]);

  const activeFilterCount = [filters.partnerCode, filters.documentType, filters.processingStatus, filters.as2MessageId, filters.correlationId, filters.receivedFrom, filters.receivedTo].filter(Boolean).length;

  async function loadPayload(document: InboundAs2DocumentSummary) {
    const requestId = ++payloadRequestId.current;
    setPayloadDocument(document);
    setPayloadDetails(null);
    setPayloadError(null);
    setPayloadLoading(true);
    try {
      const details = await getInboundDocument(document.id);
      if (requestId === payloadRequestId.current) setPayloadDetails(details);
    } catch (caught) {
      if (requestId === payloadRequestId.current) {
        setPayloadError(caught instanceof Error ? caught.message : 'Unable to load the payload.');
      }
    } finally {
      if (requestId === payloadRequestId.current) setPayloadLoading(false);
    }
  }

  function closePayload() {
    payloadRequestId.current += 1;
    setPayloadDocument(null);
    setPayloadDetails(null);
    setPayloadError(null);
    setPayloadLoading(false);
  }

  return (
    <section className="panel">
      <div className="panel-header documents-panel-header">
        <div><h2>Inbound messages</h2><p>Documents received inbound via AS2 and future channels, with processing status.</p></div>
        {data ? <span className="result-count" aria-live="polite">{data.total} message{data.total === 1 ? '' : 's'}</span> : null}
      </div>
      <div className="toolbar toolbar-grid">
        <label className="filter-field">
          <span>Partner code</span>
          <select
            value={filters.partnerCode ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, partnerCode: event.target.value }))}
          >
            <option value="">All partners</option>
            {inboundPartnerCodes.map((code) => <option key={code} value={code}>{code}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Document type</span>
          <select
            value={filters.documentType ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, documentType: event.target.value }))}
          >
            <option value="">All document types</option>
            {inboundDocumentTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Processing status</span>
          <select
            value={filters.processingStatus ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, processingStatus: event.target.value as As2InboundProcessingStatus | '' }))}
          >
            <option value="">All statuses</option>
            {processingStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
        {showAdvancedFilters ? <><label className="filter-field">
          <span>AS2 message ID</span>
          <input
            value={filters.as2MessageId ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, as2MessageId: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>Correlation ID</span>
          <input
            value={filters.correlationId ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, correlationId: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>Received from</span>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(filters.receivedFrom)}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, receivedFrom: normalizeDateTimeLocal(event.target.value) }))}
          />
        </label>
        <label className="filter-field">
          <span>Received to</span>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(filters.receivedTo)}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, receivedTo: normalizeDateTimeLocal(event.target.value) }))}
          />
        </label>
        <label className="filter-field">
          <span>Sort field</span>
          <select
            value={filters.sortBy ?? 'receivedAt'}
            onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value as NonNullable<InboundDocumentListQuery['sortBy']> }))}
          >
            <option value="receivedAt">Received</option>
            <option value="partnerCode">Partner</option>
            <option value="documentType">Document type</option>
            <option value="processingStatus">Status</option>
          </select>
        </label>
        <label className="filter-field">
          <span>Sort order</span>
          <select
            value={filters.sortOrder ?? 'desc'}
            onChange={(event) => setFilters((current) => ({ ...current, sortOrder: event.target.value as 'asc' | 'desc' }))}
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </label></> : null}
        <button type="button" className="ghost-button" aria-expanded={showAdvancedFilters} onClick={() => setShowAdvancedFilters((current) => !current)}>{showAdvancedFilters ? 'Fewer filters' : `More filters${activeFilterCount > 3 ? ` (${activeFilterCount - 3})` : ''}`}</button>
        <button type="button" onClick={() => void refresh()}>
          Refresh
        </button>
        <button
          type="button"
          className="ghost-button"
          disabled={activeFilterCount === 0 && filters.sortBy === 'receivedAt' && filters.sortOrder === 'desc'}
          onClick={() => {
            setShowAdvancedFilters(false);
            setFilters({
              page: 1,
              pageSize: 20,
              sortBy: 'receivedAt',
              sortOrder: 'desc',
              partnerCode: '',
              documentType: '',
              processingStatus: '',
              as2MessageId: '',
              correlationId: '',
              receivedFrom: '',
              receivedTo: '',
            });
          }}
        >
          Reset
        </button>
      </div>
      <div className="results-status" aria-live="polite">{isLoading ? 'Updating inbound messages…' : data ? `Showing ${data.data.length} of ${data.total} inbound messages on page ${filters.page ?? 1}.` : ''}</div>
      <div aria-busy={isLoading}>
      {isLoading ? <LoadingState label="Loading inbound messages…" /> : null}
      {error ? <ErrorState message={error} onRetry={refresh} /> : null}
      {!isLoading && !error && data && data.data.length === 0 ? <EmptyState label="No inbound messages found." /> : null}
      {!isLoading && !error && data && data.data.length > 0 ? (
        <>
          <div className="table-scroll documents-table-scroll" tabIndex={0} aria-label="Inbound messages">
          <table className="data-table documents-table data-table-cozy">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Partner</th>
                <th>AS2 message ID</th>
                <th>Status</th>
                <th>Received</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((document) => (
                <tr key={document.id}>
                  <td data-label="Document">
                    <div className="table-primary">
                      <Link to={{ pathname: `/inbound-messages/${document.id}`, search }}>{resolvedType(document)} document</Link>
                      <span className="inline-copy">
                        <span className="mono" title={document.id}>{shortId(document.id)}</span>
                        <CopyButton value={document.id} label="Copy document ID" />
                      </span>
                    </div>
                  </td>
                  <td data-label="Type">{resolvedType(document)}</td>
                  <td data-label="Partner">{document.partnerCode}</td>
                  <td data-label="AS2 message ID" className="mono" title={document.as2MessageId}>{shortId(document.as2MessageId)}</td>
                  <td data-label="Status">
                    <StatusBadge value={document.processingStatus} />
                  </td>
                  <td data-label="Received" title={formatRelativeTime(document.receivedAt)}>{new Date(document.receivedAt).toLocaleString('en-GB')}</td>
                  <td data-label="Actions" className="actions-cell inbound-message-actions">
                    <button type="button" className="table-action-button" onClick={() => void loadPayload(document)}>View payload</button>
                    <Link className="table-action-link" to={{ pathname: `/inbound-messages/${document.id}`, search }}>View details</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="pagination-bar">
            <button
              type="button"
              onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, (current.page ?? 1) - 1) }))}
              disabled={(filters.page ?? 1) <= 1}
            >
              Previous
            </button>
            <span>Page {filters.page ?? 1} · {data.total} total</span>
            <button
              type="button"
              onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}
              disabled={(data.page * data.pageSize) >= data.total}
            >
              Next
            </button>
          </div>
        </>
      ) : null}
      </div>
      <InboundPayloadDialog
        document={payloadDocument}
        details={payloadDetails}
        isLoading={payloadLoading}
        error={payloadError}
        onClose={closePayload}
        onRetry={() => { if (payloadDocument) void loadPayload(payloadDocument); }}
      />
    </section>
  );
}

// documentType is only the AS2 Subject-derived hint (frequently "UNKNOWN" -- ADII rarely sets a
// meaningful Subject header); routingTarget is the real family, re-resolved live from the stored
// payload/filename. Prefer it whenever the backend supplied one.
function resolvedType(document: InboundAs2DocumentSummary): string {
  return document.routingTarget || document.documentType;
}

function shortId(value: string): string {
  const normalized = value.replace(/^as2in_/, 'as2 ');
  return normalized.length > 18 ? `${normalized.slice(0, 18)}...` : normalized;
}

function formatRelativeTime(value: string): string {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1_000);
  const formatter = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}
